import { Bone, FindingChangesSkeleton } from "../../LoadingSkeletons";
import type { DocumentChangesView, FindingPdfEdit, OutstandingCheck } from "@repo/shared/findingChanges";
import { FileDiffIcon, ExternalLinkIcon, RefreshCwIcon, ChevronDownIcon } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "~/lib/utils";
import { useFindingChanges, documentChangesFileUrl } from "../../../../state/findingChanges";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  DialogDescription,
} from "../../../ui/dialog";
import { Collapsible, CollapsibleTrigger, CollapsiblePanel } from "../../../ui/collapsible";
import { FindingChangesNavigation } from "./FindingChangesNavigation";
import { FindingChangesToolbar } from "./FindingChangesToolbar";
import { FixChecks } from "./FixChecks";
import { ClampedText } from "../../ClampedText";
import type { PdfReviewRenderer, RenderedPdfChange } from "./pdfReviewRenderer";
import { FileSurfaceZoomControls } from "../../../files/fileSurfaceChrome";
import { useSurfaceZoom } from "../../../files/useSurfaceZoom";

function PdfScreenshot({
  result,
  side,
  highlight,
}: {
  result: RenderedPdfChange;
  side: "before" | "after";
  highlight: boolean;
}) {
  const boxes = side === "before" ? result.beforeHighlights : result.afterHighlights;
  // Theme only the screenshot: keep PDF bytes, geometry and overlay colors intact.
  return (
    <div className="relative isolate bg-white dark:bg-card">
      <img
        src={result[side]}
        alt={`${side === "before" ? "Original" : "Proposed"} PDF passage`}
        className="block h-auto w-full dark:invert dark:hue-rotate-180 dark:mix-blend-screen"
      />
      {highlight
        ? boxes.map((box, i) => (
            <span
              key={i}
              aria-hidden
              className={cn(
                "pointer-events-none absolute mix-blend-multiply dark:mix-blend-screen",
                side === "before"
                  ? "bg-red-300/40 dark:bg-red-500/25"
                  : "bg-green-300/50 dark:bg-green-500/25",
              )}
              style={{
                left: `${box.x * 100}%`,
                top: `${box.y * 100}%`,
                width: `${box.width * 100}%`,
                height: `${box.height * 100}%`,
              }}
            />
          ))
        : null}
    </div>
  );
}

/**
 * One side of a change, zoomable on its own: the label bar carries −/%/+, and
 * Ctrl/⌘+wheel, a pinch or Ctrl/⌘ with = - 0 act over the crop. The crop already
 * fills its column, so zoom never goes below 100%; above it the crop widens and
 * scrolls sideways inside its own box.
 */
function ChangeSide({
  edit,
  side,
  stacked,
  bare,
  pageUrl,
  result,
  highlight,
  onExpand,
}: {
  edit: FindingPdfEdit;
  side: "before" | "after";
  stacked: boolean;
  bare: boolean;
  pageUrl: string;
  result: RenderedPdfChange | null;
  highlight: boolean;
  onExpand: () => void;
}) {
  const zoom = useSurfaceZoom(edit, { min: 1 });
  const page = side === "before" ? edit.beforePage : edit.afterPage;
  return (
    <div
      className={cn(
        "min-w-0",
        side === "after" && "border-t border-border",
        side === "after" && !stacked && "md:border-t-0 md:border-l",
      )}
      onKeyDown={zoom.onKeyDown}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b border-border px-3 py-1 text-xs",
          bare && "sticky top-0 z-10",
          side === "before"
            ? "bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200"
            : "bg-green-50 text-green-900 dark:bg-green-950/30 dark:text-green-200",
        )}
      >
        <strong>{side === "before" ? "− Before" : "+ After"}</strong>
        <span className="flex items-center gap-2">
          {result ? (
            <FileSurfaceZoomControls
              zoom={zoom.zoom}
              label={zoom.label}
              onZoomIn={zoom.zoomIn}
              onZoomOut={zoom.zoomOut}
              onReset={zoom.reset}
              atMinimum={zoom.zoom <= 1}
            />
          ) : null}
          {bare ? null : (
            <a
              href={`${pageUrl}#page=${page}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
            >
              Page {page}
              <ExternalLinkIcon className="size-3" />
            </a>
          )}
        </span>
      </div>
      {result ? (
        <div ref={zoom.viewportRef} className="overflow-x-auto" data-change-crop={side}>
          <button
            type="button"
            className="block w-full cursor-zoom-in text-left focus-visible:outline-2 focus-visible:outline-ring"
            style={zoom.zoom > 1 ? { width: `${Math.round(zoom.zoom * 100)}%` } : undefined}
            aria-label={`Enlarge ${side} PDF screenshot: ${edit.section}`}
            onClick={onExpand}
          >
            <PdfScreenshot result={result} side={side} highlight={highlight} />
          </button>
        </div>
      ) : (
        <div role="status" aria-busy="true" className="space-y-3 bg-card p-6">
          <p className="text-xs text-muted-foreground">Preparing PDF preview…</p>
          <Bone className="h-3 w-3/4" />
          <Bone className="h-3 w-full" />
          <Bone className="h-3 w-2/3" />
        </div>
      )}
    </div>
  );
}

function ChangeRegion({
  edit,
  sectionId,
  renderer,
  rendererError,
  highlight,
  stacked,
  beforeUrl,
  afterUrl,
  onRetry,
  footer,
  collapsed = false,
  headerAction,
  tone,
  bare = false,
}: {
  edit: FindingPdfEdit;
  sectionId: string;
  renderer: PdfReviewRenderer | null;
  rendererError: string | null;
  highlight: boolean;
  stacked: boolean;
  beforeUrl: string;
  afterUrl: string;
  onRetry: () => void;
  /** Rendered under the region, inside its frame: the reviewer's decision on this edit. */
  footer?: ReactNode;
  /** Header and footer only (an edit already decided); the PDF crops are not rendered. */
  collapsed?: boolean;
  /** Replaces the header's "Proposed edit" label, e.g. with an include/leave-out control. */
  headerAction?: ReactNode;
  /** Left-out edits are muted and struck through. */
  tone?: "included" | "excluded" | undefined;
  /** Crops only: no frame, header, page links or wording; the caller frames one change at a time. */
  bare?: boolean;
}) {
  const root = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<{ result: RenderedPdfChange | null; error: string | null }>({
    result: null,
    error: null,
  });
  const [wordingOpen, setWordingOpen] = useState(false);
  const [expanded, setExpanded] = useState<"before" | "after" | null>(null);
  useEffect(() => {
    if (!root.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || !renderer || collapsed) return;
    let active = true;
    void renderer.render(edit).then(
      (result) => {
        if (active) setState({ result, error: null });
      },
      (error) => {
        if (active)
          setState({
            result: null,
            error: error instanceof Error ? error.message : "The PDF preview could not be loaded.",
          });
      },
    );
    return () => {
      active = false;
    };
  }, [visible, renderer, edit, collapsed]);
  return (
    <section
      ref={root}
      id={sectionId}
      style={{ scrollMarginTop: "24px" }}
      tabIndex={-1}
      className={cn(
        "scroll-mt-40 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 sm:scroll-mt-32",
        !bare && "overflow-hidden rounded-lg border border-border",
        tone === "excluded" && "bg-muted/30",
      )}
      aria-label={`${edit.section}, page ${edit.beforePage}`}
    >
      {bare ? null : <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-3">
        <h3 className="flex min-w-0 items-center gap-3 text-sm font-medium">
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            PAGE {edit.beforePage}
            {edit.afterPage !== edit.beforePage ? ` → ${edit.afterPage}` : ""}
          </span>
          <span className={cn("break-words", tone === "excluded" && "text-muted-foreground line-through")}>{edit.section}</span>
        </h3>
        {headerAction ?? (
          <span className="text-xs text-muted-foreground">
            {!edit.beforeText.trim()
              ? "Added passage"
              : !edit.afterText.trim()
                ? "Removed passage"
                : "Proposed edit"}
          </span>
        )}
      </header>}
      {collapsed ? null : (<>
      {state.result?.fullPage && !bare ? (
        <p role="status" className="border-b border-border px-4 py-3 text-xs text-muted-foreground">
          Showing full pages because an exact passage could not be matched. Only verified passages
          are highlighted; compare the wording below.
        </p>
      ) : null}
      {state.error || rendererError ? (
        <div className="space-y-3 px-4 py-5">
          <p role="status" className="text-sm text-muted-foreground">
            {state.error || rendererError}
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon className="size-3.5" /> Retry PDF preview
          </Button>
        </div>
      ) : (
        <div className={cn("grid", !stacked && "md:grid-cols-2")}>
          {(["before", "after"] as const).map((side) => (
            <ChangeSide
              key={side}
              edit={edit}
              side={side}
              stacked={stacked}
              bare={bare}
              pageUrl={side === "before" ? beforeUrl : afterUrl}
              result={state.result}
              highlight={highlight}
              onExpand={() => setExpanded(side)}
            />
          ))}
        </div>
      )}
      {bare ? null : <Collapsible
        open={wordingOpen}
        onOpenChange={setWordingOpen}
        className="border-t border-border text-xs text-muted-foreground"
      >
        <CollapsibleTrigger className="group flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring">
          <ChevronDownIcon className="size-3.5 -rotate-90 transition-transform group-data-panel-open:rotate-0 motion-reduce:transition-none" />
          Read the wording change
        </CollapsibleTrigger>
        <CollapsiblePanel className="motion-reduce:transition-none">
          <div className="grid gap-4 px-4 pb-4 text-sm md:grid-cols-2">
            <div>
              <p className="font-medium text-red-700 dark:text-red-300">− Before</p>
              <p className="mt-1 whitespace-pre-wrap break-words">
                {edit.beforeText || "No passage in the original."}
              </p>
            </div>
            <div>
              <p className="font-medium text-green-700 dark:text-green-300">+ After</p>
              <p className="mt-1 whitespace-pre-wrap break-words">
                {edit.afterText || "Passage removed in the proposal."}
              </p>
            </div>
          </div>
        </CollapsiblePanel>
      </Collapsible>}
      </>)}
      <Dialog
        open={expanded !== null}
        onOpenChange={(open) => {
          if (!open) setExpanded(null);
        }}
      >
        <DialogPopup className="max-w-[min(96vw,1400px)]">
          <DialogHeader>
            <DialogTitle>
              {edit.section} · {expanded === "before" ? "Before" : "After"}
            </DialogTitle>
            <DialogDescription>
              Page {expanded === "before" ? edit.beforePage : edit.afterPage}. Screenshot of the{" "}
              {expanded === "before" ? "original" : "proposed"} PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-auto">
            {state.result && expanded ? (
              <div className="w-full">
                <PdfScreenshot result={state.result} side={expanded} highlight={highlight} />
              </div>
            ) : null}
          </div>
        </DialogPopup>
      </Dialog>
      {footer}
    </section>
  );
}

export function FindingPdfChangesReview({
  organizationId,
  proposal,
  currentVersion,
  status,
  renderEditFooter,
  renderChecks,
  isEditCollapsed,
  showLegend = true,
  renderEditHeader,
  editTone,
  bare = false,
}: {
  organizationId: string;
  proposal: DocumentChangesView;
  currentVersion: number | null;
  /** Replaces the "Draft · awaiting review" pill, e.g. "Applied as File v4". */
  status?: ReactNode;
  /** A decision row under each edit (keep or drop, why, redo). */
  renderEditFooter?: (edit: FindingPdfEdit, index: number) => ReactNode;
  /** Replaces the read-only "Before approval" list, e.g. with checkboxes. */
  renderChecks?: (checks: ReadonlyArray<OutstandingCheck>) => ReactNode;
  /** Collapse an edit to its header and footer, e.g. once it is kept or dropped. */
  isEditCollapsed?: (edit: FindingPdfEdit, index: number) => boolean;
  /** The "− Before and + After show the actual PDF pages" line. */
  showLegend?: boolean;
  /** Replaces each edit header's right-hand label. */
  renderEditHeader?: (edit: FindingPdfEdit, index: number) => ReactNode;
  editTone?: (edit: FindingPdfEdit, index: number) => "included" | "excluded" | undefined;
  /** The crops only, for a caller that shows one change at a time with its own frame and controls. */
  bare?: boolean;
}) {
  const sectionIdPrefix = useId();
  const [highlight, setHighlight] = useState(true);
  const [stacked, setStacked] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [renderer, setRenderer] = useState<PdfReviewRenderer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const beforeUrl = documentChangesFileUrl(organizationId, proposal, "original");
  const afterUrl = documentChangesFileUrl(organizationId, proposal, "proposed");
  useEffect(() => {
    let disposed = false;
    setRenderer(null);
    setError(null);
    let instance: PdfReviewRenderer | null = null;
    void import("./pdfReviewRenderer")
      .then((module) => {
        if (disposed) return;
        instance = module.createPdfReviewRenderer(beforeUrl, afterUrl);
        setRenderer(instance);
      })
      .catch(() => {
        if (!disposed)
          setError(
            "The PDF renderer could not load. You can still read the proposed wording or open each PDF.",
          );
      });
    return () => {
      disposed = true;
      instance?.destroy();
    };
  }, [beforeUrl, afterUrl, attempt]);
  return (
    <div className={cn(!bare && "space-y-6")} data-testid="finding-pdf-changes">
      {bare ? null : <><section className="space-y-4 rounded-xl border border-border/60 bg-card p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="flex min-w-0 items-start gap-2 break-words text-sm font-medium">
              <FileDiffIcon className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 break-words">{proposal.sourceFilename}</span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Original v{proposal.sourceVersion} → proposed PDF ·{" "}
              {new Date(proposal.createdAt).toLocaleDateString()}
            </p>
          </div>
          {status === undefined ? (
            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-800 dark:text-amber-200">
              Draft · awaiting review
            </span>
          ) : (
            status
          )}
        </div>
        <div className="text-sm leading-6">
          <ClampedText lines={3} className="text-muted-foreground">{proposal.summary}</ClampedText>
        </div>
        {currentVersion !== null && currentVersion !== proposal.sourceVersion ? (
          <p
            role="status"
            className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm"
          >
            The policy is now v{currentVersion}. This proposal compares the original v
            {proposal.sourceVersion}; review it against the current policy before applying changes.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            render={<a href={afterUrl} target="_blank" rel="noopener noreferrer" />}
          >
            Open proposed PDF
            <ExternalLinkIcon className="size-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            render={<a href={beforeUrl} target="_blank" rel="noopener noreferrer" />}
          >
            Open original
          </Button>
        </div>
      </section>
      <FindingChangesToolbar
        edits={proposal.edits}
        highlight={highlight}
        onHighlightChange={setHighlight}
        stacked={stacked}
        onStackedChange={setStacked}
        navigation={
          proposal.edits.length > 1 ? (
            <FindingChangesNavigation
              edits={proposal.edits}
              sectionIdPrefix={sectionIdPrefix}
              checksCount={proposal.outstandingChecks.length}
            />
          ) : undefined
        }
      />
      {showLegend ? <p className="text-xs text-muted-foreground">
        <span className="font-medium text-red-700 dark:text-red-300">− Before</span> and{" "}
        <span className="font-medium text-green-700 dark:text-green-300">+ After</span> show the
        actual PDF pages. Select a passage to enlarge it.
      </p> : null}</>}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {proposal.edits.map((edit, index) => (
        <ChangeRegion
          key={`${proposal.id}:${attempt}:${index}:${edit.section}`}
          edit={edit}
          sectionId={`${sectionIdPrefix}-${index}`}
          renderer={renderer}
          rendererError={error}
          highlight={highlight}
          stacked={stacked}
          beforeUrl={beforeUrl}
          afterUrl={afterUrl}
          onRetry={() => {
            setRenderer(null);
            setAttempt((value) => value + 1);
          }}
          footer={renderEditFooter?.(edit, index)}
          collapsed={isEditCollapsed?.(edit, index) ?? false}
          headerAction={renderEditHeader?.(edit, index)}
          tone={editTone?.(edit, index)}
          bare={bare}
        />
      ))}
      {!bare && proposal.outstandingChecks.length ? (
        <section
          id={`${sectionIdPrefix}-checks`}
          style={{ scrollMarginTop: "24px" }}
          tabIndex={-1}
          className="scroll-mt-40 rounded-xl border border-border/60 bg-card p-5 outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <h3 className="text-sm font-semibold">Before applying</h3>
          {renderChecks ? renderChecks(proposal.outstandingChecks) : <FixChecks checks={proposal.outstandingChecks} />}
        </section>
      ) : null}
    </div>
  );
}

export function FindingChangesPanel({
  organizationId,
  policyId,
  findingId,
  currentVersion,
  requiredChanges,
  reviewKind = "finding",
}: {
  organizationId: string;
  policyId: string;
  findingId: string;
  currentVersion: number | null;
  requiredChanges: ReadonlyArray<string>;
  reviewKind?: "finding" | "recommendation";
}) {
  const { proposal, loading, unavailable, refresh } = useFindingChanges(
    organizationId,
    policyId,
    findingId,
    reviewKind,
  );
  if (loading) return <FindingChangesSkeleton />;
  if (unavailable)
    return (
      <div role="alert" className="space-y-3 py-6">
        <p className="text-sm text-muted-foreground">Proposed changes could not be loaded.</p>
        <Button size="sm" variant="outline" onClick={refresh}>
          <RefreshCwIcon className="size-3.5" />
          Retry
        </Button>
      </div>
    );
  if (proposal)
    return (
      <FindingPdfChangesReview
        key={`${organizationId}:${proposal.id}`}
        organizationId={organizationId}
        proposal={proposal}
        currentVersion={currentVersion}
      />
    );
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-dashed border-border p-6">
        <FileDiffIcon className="size-5 text-muted-foreground" />
        <h2 className="mt-3 text-sm font-semibold">No PDF proposal yet</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          When Inspector attaches a proposed PDF, its changes will appear here with before-and-after
          screenshots.
        </p>
      </div>
      {requiredChanges.length ? (
        <section>
          <h3 className="text-sm font-semibold">Required changes</h3>
          <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {requiredChanges.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
